import { useTranslation } from 'react-i18next';

import type { QuestionScoreRow } from './types';

import { Badge } from '~/components/ui/badge';

interface AssessmentResultsProps {
  questions: QuestionScoreRow[];
}

function getScoreColor(score: number): string {
  if (score >= 4) return 'text-green-600';
  if (score >= 3) return 'text-yellow-600';
  return 'text-red-600';
}

export function AssessmentResults({ questions }: AssessmentResultsProps): React.ReactNode {
  const { t } = useTranslation();

  if (questions.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">{t('scoring.assessment')}</h2>
      </div>
      <div className="flex flex-col gap-2">
        {questions.map((q) => (
          <div
            key={q.id}
            className="rounded-lg border border-border bg-card p-4 shadow-sm border-l-4 border-l-[var(--color-section-orange)]"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-1">
                {q.competencyName && (
                  <Badge variant="outline" className="w-fit text-xs">
                    {q.competencyName}
                  </Badge>
                )}
                <p className="text-sm font-medium">{q.questionText}</p>
              </div>
              {q.score !== null && (
                <div className="flex shrink-0 items-center gap-1">
                  <span className={`text-xl font-semibold ${getScoreColor(q.score)}`}>
                    {q.score}
                  </span>
                  <span className="text-sm text-muted-foreground">/ 5</span>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-col gap-1">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">{t('scoring.reasoning')}:</span> {q.reasoning}
              </p>
              <p className="text-sm italic text-muted-foreground">&ldquo;{q.evidence}&rdquo;</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
